import { BriefcaseBusiness, ClipboardList, Copy, DoorOpen, Handshake, LayoutGrid, Volume2, Wifi, WifiOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AssetDrawer } from './AssetDrawer.jsx';
import { Board } from './Board.jsx';
import { CardReveal } from './CardReveal.jsx';
import { EventLog } from './EventLog.jsx';
import { GameControls } from './GameControls.jsx';
import { PlayersPanel } from './PlayersPanel.jsx';
import { PropertyPanel } from './PropertyPanel.jsx';
import { TradeModal } from './TradeModal.jsx';
import { TradeNotice } from './TradeNotice.jsx';
import { formatMoney } from '../utils/board.js';

export function GameScreen({ room, action, connected, leaveLocalSession, onReturn }) {
  const { game, code, viewerId, hostId, isSpectator } = room;
  const [selection, setSelection] = useState({ index: null, diceSequence: -1 });
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [cellDetails, setCellDetails] = useState(null);
  const selectedCell = selection.diceSequence === game.dice.sequence && selection.index != null
    ? selection.index
    : (game.currentPlayer?.position ?? 0);

  useEffect(() => {
    document.documentElement.classList.add('game-active');
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }));
    return () => {
      document.documentElement.classList.remove('game-active');
      window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const handleNativeBack = (event) => {
      if (!assetsOpen && !tradeOpen && !logOpen && cellDetails === null) return;
      event.preventDefault();
      setAssetsOpen(false);
      setTradeOpen(false);
      setLogOpen(false);
      setCellDetails(null);
    };
    window.addEventListener('kapitalia:back', handleNativeBack);
    return () => window.removeEventListener('kapitalia:back', handleNativeBack);
  }, [assetsOpen, tradeOpen, logOpen, cellDetails]);

  const leave = () => {
    if (room.offline || onReturn) { (onReturn ?? leaveLocalSession)(); return; }
    if (window.confirm('Удалить сохранённый вход на этом устройстве? Вернуться без токена уже не получится.')) leaveLocalSession();
  };

  const showBoard = () => {
    setAssetsOpen(false);
    setTradeOpen(false);
    setLogOpen(false);
    setCellDetails(null);
  };

  return (
    <div className="game-shell">
      <header className="game-header">
        <div className="brand-lockup"><span className="brand-mark" aria-hidden="true"><i /><i /><i /></span><span>Капиталия</span></div>
        <button className="header-room" type="button" onClick={() => !room.offline && navigator.clipboard?.writeText(code)} title={room.offline ? 'Локальная игра' : 'Скопировать код комнаты'}>{room.offline ? (room.mode === 'hotseat' ? 'Одно устройство' : 'С ботами') : 'Комната'} <strong>{room.offline ? '' : code}</strong>{!room.offline && <Copy size={15} />}</button>
        <span className={`header-connection ${connected ? '' : 'is-offline'}`}>{connected && !room.offline ? <Wifi size={15} /> : <WifiOff size={15} />}{room.offline ? 'Офлайн' : connected ? 'Онлайн' : 'Переподключение'}</span>
        <div className="game-header__actions">
          <button type="button" title="Звук"><Volume2 size={18} /><span>Звук</span></button>
          <button type="button" onClick={leave} title="Выйти"><DoorOpen size={18} /><span>Выйти</span></button>
        </div>
      </header>

      <TradeNotice game={game} viewerId={viewerId} action={action} />
      <main className="game-layout">
        <div className="game-layout__board">
          <Board game={game} snapshotEpoch={room.snapshotEpoch} selectedCell={selectedCell} onSelectCell={(index) => {
            setSelection({ index, diceSequence: game.dice.sequence });
            if (window.matchMedia('(max-width: 620px)').matches) setCellDetails(index);
          }} />
        </div>
        <aside className="game-sidebar">
          <PlayersPanel game={game} viewerId={viewerId} hostId={hostId} />
          <GameControls game={game} viewerId={viewerId} action={action} />
          <div className="game-context">
            <PropertyPanel game={game} cellIndex={selectedCell} />
            <button className="mobile-cell-summary" type="button" onClick={() => setCellDetails(selectedCell)} aria-label="Информация о клетке">
              <span>Клетка {selectedCell}</span><strong>{game.board[selectedCell]?.name}</strong>
              {game.board[selectedCell]?.price ? <b>{formatMoney(game.board[selectedCell].price)}</b> : null}
            </button>
            <div className="mobile-player-status" aria-label="Другие игроки">
              {game.players.filter((player) => player.id !== viewerId && !player.bankrupt).map((player) => (
                <span key={player.id}><i style={{ background: player.color }} /><strong>{player.name}</strong><span>{player.connected ? 'Онлайн' : 'Нет связи'}</span><b>{formatMoney(player.money)}</b></span>
              ))}
            </div>
            <div className="desktop-event-log"><EventLog log={game.log} /></div>
            <ol className="mobile-context-log" aria-label="Последние события" aria-live="polite">
              {game.log.slice(-2).map((entry) => <li key={entry.id}>{entry.message}</li>)}
            </ol>
          </div>
        </aside>
      </main>

      <nav className="mobile-nav" aria-label="Игровые разделы">
        <button className={!assetsOpen && !tradeOpen && !logOpen ? 'is-active' : ''} type="button" onClick={showBoard}><LayoutGrid size={21} /><span>Поле</span></button>
        <button disabled={isSpectator} className={assetsOpen ? 'is-active' : ''} type="button" onClick={() => { setAssetsOpen(true); setTradeOpen(false); setLogOpen(false); setCellDetails(null); }}><BriefcaseBusiness size={21} /><span>Имущество</span></button>
        <button disabled={isSpectator} className={tradeOpen ? 'is-active' : ''} type="button" onClick={() => { setTradeOpen(true); setAssetsOpen(false); setLogOpen(false); setCellDetails(null); }}><Handshake size={21} /><span>Сделки</span></button>
        <button className={logOpen ? 'is-active' : ''} type="button" onClick={() => { setLogOpen(true); setAssetsOpen(false); setTradeOpen(false); setCellDetails(null); }}><ClipboardList size={21} /><span>Журнал</span></button>
      </nav>

      <div className="desktop-quick-actions">
        <button disabled={isSpectator} className="button button--secondary" type="button" onClick={() => setAssetsOpen(true)}><BriefcaseBusiness size={17} /> Имущество</button>
        <button disabled={isSpectator} className="button button--secondary" type="button" onClick={() => setTradeOpen(true)}><Handshake size={17} /> Сделка</button>
      </div>

      <AssetDrawer open={assetsOpen} onClose={() => setAssetsOpen(false)} game={game} viewerId={viewerId} action={action} />
      {tradeOpen && <TradeModal open onClose={() => setTradeOpen(false)} game={game} viewerId={viewerId} action={action} />}
      {logOpen && (
        <section className="mobile-fullscreen mobile-log-screen" aria-label="Журнал игры">
          <header className="drawer__header"><div><span>Хронология матча</span><h2>Журнал игры</h2></div><button type="button" onClick={() => setLogOpen(false)} aria-label="Вернуться на поле"><X /></button></header>
          <EventLog log={game.log} />
        </section>
      )}
      {cellDetails !== null && (
        <section className="mobile-fullscreen mobile-cell-details" aria-label="Подробности клетки">
          <header className="drawer__header"><h2>Клетка {cellDetails}</h2><button type="button" onClick={() => setCellDetails(null)} aria-label="Вернуться на поле"><X /></button></header>
          <PropertyPanel game={game} cellIndex={cellDetails} />
        </section>
      )}
      <CardReveal card={game.lastCard} />
    </div>
  );
}
